import { ReactNode, useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layers, Gamepad2, Cpu, BookOpen, ChevronDown, Crosshair, Bug, Sparkles, Flower2, Volume2, VolumeX } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme, themeNames, Theme } from '../contexts/ThemeContext';
import { useSoundHandlers, useSoundMute } from '../hooks/useSoundEffects';

interface LayoutProps {
  children: ReactNode;
}

// 每个主题对应的图标
const themeIcons: Record<Theme, typeof Cpu> = {
  cyber: Cpu,
  esports: Gamepad2,
  ink: BookOpen,
  qinghua: Flower2,
  terran: Crosshair,
  zerg: Bug,
  protoss: Sparkles,
};

export default function Layout({ children }: LayoutProps) {
  const { theme, setTheme, availableThemes } = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { playHover, playClick } = useSoundHandlers();
  const { isMuted, toggleMute } = useSoundMute();

  const ThemeIcon = themeIcons[theme];

  // 点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleThemeSelect = (newTheme: Theme) => {
    playClick();
    setTheme(newTheme);
    setIsOpen(false);
  };

  const handleDropdownToggle = () => {
    playClick();
    setIsOpen(!isOpen);
  };

  const isQinghua = theme === 'qinghua';

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--theme-bg-primary)' }}>
      {/* 背景效果 */}
      <div className="fixed inset-0 bg-grid opacity-50 pointer-events-none" />
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: `radial-gradient(ellipse at center, var(--theme-bg-secondary) 0%, var(--theme-bg-primary) 70%)`
        }}
      />
      
      {/* 顶部导航 */}
      <header 
        className="relative z-50" 
        style={{ 
          borderBottom: isQinghua ? 'none' : '1px solid var(--theme-border)',
          background: isQinghua ? '#FFFDF8' : 'transparent'
        }}
      >
        {/* 青花瓷顶部装饰纹样 */}
        {isQinghua && (
          <div 
            className="absolute bottom-0 left-0 right-0 h-1"
            style={{
              background: `repeating-linear-gradient(
                90deg,
                transparent 0px,
                transparent 20px,
                #2E5AA8 20px,
                #2E5AA8 25px,
                transparent 25px,
                transparent 30px,
                #4A7AC7 30px,
                #4A7AC7 32px,
                transparent 32px,
                transparent 50px
              )`
            }}
          />
        )}
        
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 group">
            <motion.div
              whileHover={{ rotate: isQinghua ? 0 : 180, scale: isQinghua ? 1.05 : 1 }}
              transition={{ duration: 0.5 }}
              className={`w-10 h-10 rounded-full flex items-center justify-center ${isQinghua ? 'qinghua-plate' : ''}`}
              style={isQinghua ? {} : {
                background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accent-secondary) 100%)`
              }}
            >
              <Layers className="w-5 h-5" style={{ color: isQinghua ? '#2E5AA8' : 'var(--theme-bg-primary)' }} />
            </motion.div>
            <div>
              <h1 
                className={`text-xl font-bold ${isQinghua ? '' : 'font-display glow-text'}`} 
                style={{ 
                  color: 'var(--theme-accent)',
                  fontFamily: isQinghua ? "'Ma Shan Zheng', 'Noto Serif SC', serif" : undefined,
                  fontSize: isQinghua ? '1.5rem' : undefined
                }}
              >
                剥洋葱
              </h1>
              <p className="text-xs font-body" style={{ color: 'var(--theme-text-muted)' }}>
                {isQinghua ? '长难句结构解析' : 'Sentence Structure Analysis'}
              </p>
            </div>
          </Link>

          {/* 右上角控件区域 */}
          <div className="flex items-center gap-3">
            {/* 音效开关按钮 */}
            <motion.button
              onClick={toggleMute}
              onMouseEnter={playHover}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.95 }}
              className="w-9 h-9 rounded-lg flex items-center justify-center transition-colors duration-200"
              style={{
                background: 'var(--theme-bg-card)',
                border: '1px solid var(--theme-border)',
              }}
              title={isMuted ? '开启音效' : '关闭音效'}
            >
              {isMuted ? (
                <VolumeX className="w-4 h-4" style={{ color: 'var(--theme-text-muted)' }} />
              ) : (
                <Volume2 className="w-4 h-4" style={{ color: 'var(--theme-accent)' }} />
              )}
            </motion.button>

            {/* 主题下拉菜单 */}
            <div className="theme-dropdown" ref={dropdownRef}>
              <motion.button
                onClick={handleDropdownToggle}
                onMouseEnter={playHover}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="theme-dropdown-trigger"
              >
                <ThemeIcon className="w-4 h-4" style={{ color: 'var(--theme-accent)' }} />
                <span className="hidden sm:inline font-body flex-1 text-left">{themeNames[theme]}</span>
                <ChevronDown 
                  className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                  style={{ color: 'var(--theme-text-muted)' }}
                />
              </motion.button>

            <AnimatePresence>
              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  className="theme-dropdown-menu"
                >
                  {availableThemes.map((t) => {
                    const Icon = themeIcons[t];
                    const isActive = t === theme;
                    return (
                      <motion.button
                        key={t}
                        onClick={() => handleThemeSelect(t)}
                        onMouseEnter={playHover}
                        whileHover={{ x: 4 }}
                        className={`theme-dropdown-item w-full ${isActive ? 'active' : ''}`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-body">{themeNames[t]}</span>
                        {isActive && (
                          <span className="ml-auto text-xs" style={{ color: 'var(--theme-accent)' }}>✓</span>
                        )}
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}
            </AnimatePresence>
            </div>
          </div>
        </nav>
      </header>

      {/* 主内容区 */}
      <main className="relative z-10">
        {children}
      </main>

      {/* 底部装饰 */}
      <footer 
        className="relative z-10 mt-20" 
        style={{ 
          borderTop: isQinghua ? 'none' : '1px solid var(--theme-border)',
          background: isQinghua ? '#FFFDF8' : 'transparent'
        }}
      >
        {/* 青花瓷底部装饰纹样 */}
        {isQinghua && (
          <div 
            className="absolute top-0 left-0 right-0 h-1"
            style={{
              background: `repeating-linear-gradient(
                90deg,
                transparent 0px,
                transparent 20px,
                #2E5AA8 20px,
                #2E5AA8 25px,
                transparent 25px,
                transparent 30px,
                #4A7AC7 30px,
                #4A7AC7 32px,
                transparent 32px,
                transparent 50px
              )`
            }}
          />
        )}
        <div className="max-w-7xl mx-auto px-6 py-6 text-center">
          <p 
            className="text-sm" 
            style={{ 
              color: 'var(--theme-text-muted)',
              fontFamily: isQinghua ? "'Noto Serif SC', serif" : undefined
            }}
          >
            Powered by <span style={{ color: 'var(--theme-accent)' }}>zxNLP</span> · 长难句五步分析法
          </p>
        </div>
      </footer>
    </div>
  );
}
